import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trash2, Mail, Phone, MessageSquare, Download, ClipboardList } from 'lucide-react';
import CreateOrderDialog from '@/components/admin/CreateOrderDialog';
import { exportToCsv } from '@/lib/csvExport';

interface Lead {
  id: string;
  email: string | null;
  phone: string | null;
  conversation: string | null;
  created_at: string;
}

const ChatbotLeadsAdmin = () => {
  const [leads, setLeads] = useState<Lead[]>([]);

  const fetchLeads = async () => {
    const { data } = await supabase.from('chatbot_leads').select('*').order('created_at', { ascending: false });
    if (data) setLeads(data);
  };

  useEffect(() => { fetchLeads(); }, []);

  const deleteLead = async (id: string) => {
    await supabase.from('chatbot_leads').delete().eq('id', id);
    toast.success('Smazáno');
    fetchLeads();
  };

  const exportCsv = () => {
    exportToCsv('leady.csv', leads, [
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Telefon' },
      { key: 'conversation', label: 'Konverzace' },
      { key: 'created_at', label: 'Vytvořeno' },
    ]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <h1 className="text-4xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
          Chatbot Leady
        </h1>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download size={14} className="mr-1" /> Export CSV
        </Button>
      </div>

      {leads.length === 0 ? (
        <p className="text-muted-foreground">Zatím žádné leady.</p>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <div key={lead.id} className="p-4 bg-card border border-border rounded-md flex gap-4">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-4 text-sm">
                  {lead.email && (
                    <span className="flex items-center gap-1 text-foreground">
                      <Mail size={14} className="text-primary" /> {lead.email}
                    </span>
                  )}
                  {lead.phone && (
                    <span className="flex items-center gap-1 text-foreground">
                      <Phone size={14} className="text-primary" /> {lead.phone}
                    </span>
                  )}
                  <span className="text-muted-foreground text-xs">
                    {new Date(lead.created_at).toLocaleString('cs-CZ')}
                  </span>
                </div>
                {lead.conversation && (
                  <details className="mt-2">
                    <summary className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                      <MessageSquare size={12} /> Zobrazit konverzaci
                    </summary>
                    <pre className="mt-2 text-xs text-foreground/80 whitespace-pre-wrap bg-background p-3 rounded border border-border max-h-48 overflow-y-auto">
                      {lead.conversation}
                    </pre>
                  </details>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <CreateOrderDialog
                  initial={{
                    client_name: lead.email || lead.phone || '',
                    client_email: lead.email || '',
                    client_phone: lead.phone || '',
                    notes: lead.conversation || '',
                    source_lead_id: lead.id,
                  }}
                  trigger={
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary" title="Převést na zakázku">
                      <ClipboardList size={18} />
                    </Button>
                  }
                />
                <Button variant="ghost" size="icon" onClick={() => deleteLead(lead.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatbotLeadsAdmin;
